import modalStyle from 'components/common/root-modal/styles/RootModalChild.module.scss';
import { KiteAlert, KiteTextInput } from '@kite/react-kite';
import { useAppDispatch, useAppSelector } from 'hooks';
import { updateIsOpen, updateToastMessage } from 'slices/app';
import SaveCancel from 'components/save-cancel';
import { useUpdateLabelMutation } from 'generated/graphql';
import React, { useEffect, useState } from 'react';
import { TrackingHook, useTracking } from 'react-tracking';
import { isValidLabelName, isDuplicate } from 'utils/labelNameValidator';
import { updateLabelThunk } from 'thunks/contact/updateLabelThunk';
import { appConstants } from 'appConstants';
import ModalContentWrapper from 'components/common/root-modal/ModalContentWrapper';
import { useTarget } from 'components/common/focus/FocusContext';
import { ValidationKeys, ErrorsKeys, useDialogTranslation } from 'translation/hooks';

interface RenameLabelProps {
  labelId: string;
  labelName: string;
}

const RenameLabel: React.FC<RenameLabelProps> = ({ labelId, labelName }) => {
  const { t } = useDialogTranslation('renameLabelCopy');
  const { trackEvent }: TrackingHook = useTracking({ page: 'labels' });
  const apiError = useAppSelector((state) => state.labelSlice.apiError);
  const labelCollection = useAppSelector((state) => state.labelSlice.labelCollection);
  const [validationErrorMessage, setValidationErrorMessage] = useState<ValidationKeys | ''>('');
  const [alertDescription, setAlertDescription] = useState<ErrorsKeys | ''>('');
  const [draft, setDraft] = useState(labelName || '');
  const focusOpener = useTarget('modal');
  const dispatch = useAppDispatch();
  const [updateLabelHook] = useUpdateLabelMutation();

  useEffect(() => {
    trackEvent({ event: 'viewRenameLabel' });
  }, []);

  useEffect(() => {
    if (apiError?.message) {
      setAlertDescription('unableToSaveErr');
    }
  }, [apiError]);

  const handleInputTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setDraft(e.target.value);
    validateInput(e.target.value);
  };

  const isSaved = async (): Promise<boolean> => {
    trackEvent({ event: 'renameLabelSave' });
    const result = await dispatch(
      updateLabelThunk({
        payload: {
          id: labelId,
          label: {
            name: draft,
          },
        },
        updateLabelHook,
      })
    ).unwrap();

    if (result.success) {
      trackEvent({ event: 'renameLabelSuccess' });
      setDraft('');
      dispatch(updateToastMessage({ message: t('successToast'), success: true }));
      return true;
    }

    trackEvent({ event: 'renameErrorApi' });
    return false;
  };

  const isCancelled = (): boolean => {
    trackEvent({ event: 'renameLabelCancel' });
    setDraft('');
    setValidationErrorMessage('');
    dispatch(updateIsOpen(false));
    focusOpener();

    return true;
  };

  const validateInput = (input: string): void => {
    if (!isValidLabelName(input)) {
      trackEvent({ event: 'renameErrorValidation' });
      setValidationErrorMessage('invalidCharacters');
      return;
    }

    if (isDuplicate(input, labelCollection)) {
      trackEvent({ event: 'renameErrorDuplicate' });
      setValidationErrorMessage('labelDuplicate');
      return;
    }

    if (input && input.length > appConstants.MAX_LABEL_NAME_LENGTH) {
      setValidationErrorMessage('labelCharacterLimit');
      return;
    }

    setValidationErrorMessage('');
  };

  const isChanged = (): boolean => {
    if (!draft.length || draft === labelName) {
      return false;
    }

    return true;
  };

  const saveCancelProps = {
    isSaved,
    isCancelled,
    isValidate: () => !validationErrorMessage,
    draft,
    isChanged,
    saveButtonText: t('confirmBtnText'),
  };

  return (
    <ModalContentWrapper>
      <h5>{t('header')}</h5>
      <p>{t('description')}</p>
      {apiError ? (
        <KiteAlert className={modalStyle.alert} type="error" description={t(alertDescription)} level="page" />
      ) : null}
      <KiteTextInput
        className={modalStyle.textInput}
        label={t('inputLabel')}
        onChange={handleInputTextChange}
        value={draft}
        errorMessage={t(validationErrorMessage)}
        ariaLive="polite"
      />
      <SaveCancel {...saveCancelProps} />
    </ModalContentWrapper>
  );
};

export default RenameLabel;
